import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

const errorData = defineMessages({
  account_number_au_empty_error: {
    id: 'shared_components.error_data.account_number_au_empty_error',
    defaultMessage: 'Account number is required.'
  },
  account_number_au_format_error: {
    id: 'shared_components.error_data.account_number_au_format_error',
    defaultMessage: 'Enter your Account Number as 5-9 numbers without punctuation.'
  },
  attendees_empty: {
    id: 'shared_components.error_data.attendees_empty_error',
    defaultMessage: 'Please enter the number of attendees.'
  },
  attendees_range_error: {
    id: 'shared_components.error_data.attendees_range_error',
    defaultMessage: 'The number of attendees must be between 1 and 100,000.'
  },
  board_name_empty_error: {
    id: 'shared_components.input_data.board_name_empty_error',
    defaultMessage: 'Please enter the name of your project.'
  },
  board_name_max: {
    id: 'shared_components.input_data.board_name_max_error',
    defaultMessage: 'Maximum is 35 characters.'
  },
  board_name_min: {
    id: 'shared_components.input_data.board_name_min_error',
    defaultMessage: 'Minimum is 2 characters.'
  },
  bsb_number_empty_error: {
    id: 'shared_components.error_data.bsb_number_empty_error',
    defaultMessage: 'BSB is required.'
  },
  bsb_number_format_error: {
    id: 'shared_components.error_data.bsb_number_format_error',
    defaultMessage: 'Enter your BSB as 6 numbers without punctuation.'
  },
  email_empty_error: {
    id: 'shared_components.input_data.email_empty_error',
    defaultMessage: 'Please enter your email.'
  },
  email_valid_error: {
    id: 'shared_components.input_data.email_valid_error',
    defaultMessage: 'Please enter a valid email.'
  },
  email_invalid_code_error: {
    id: 'shared_components.verification_form.invalid_code_error',
    defaultMessage: 'Please enter a valid 4-digit code.'
  },
  enter_name_error: {
    id: 'shared_components.error_data.enter_name',
    defaultMessage: 'Enter cardholder\'s name and last name.'
  },
  empty_input_error: {
    id: 'shared_components.error_data.empty_cancellation_reason_error',
    defaultMessage: 'Please fill in your response'
  },
  iban_empty_error: {
    id: 'shared_components.error_data.iban_empty_error',
    defaultMessage: 'IBAN is required.'
  },
  iban_format_error: {
    id: 'shared_components.error_data.iban_format_error',
    defaultMessage: 'Enter your IBAN of up to 34 characters without punctuation.'
  },
  msg_host: {
    id: 'shared_components.error_data.msg_host_empty_error',
    defaultMessage: 'Please enter a message.'
  },
  password_empty_error: {
    id: 'shared_components.input_data.password_empty_error',
    defaultMessage: 'Please enter your password.'
  },
  password_format_error: {
    id: 'shared_components.input_data.password_format_error',
    defaultMessage: 'Your password needs to be at least 8 characters long and include a number.'
  },
  personal_message: {
    id: 'shared_components.input_data.personal_message_error',
    defaultMessage: 'Please enter a personal message.'
  },
  personal_message_max: {
    id: 'shared_components.input_data.personal_message_max_error',
    defaultMessage: 'Maximum is 1000 characters'
  },
  personal_message_min: {
    id: 'shared_components.input_data.personal_message_min_error',
    defaultMessage: 'Minimum is 15 characters.'
  },
  comment_empty_error: {
    id: 'shared_components.input_data.comment_empty_error',
    defaultMessage: 'Please enter a comment'
  }
});

export const InputError = {
  default_input: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  default_input_number: {
    error: {
      constraints: {
        presence: 'optional',
        custom: true
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  default_textarea: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  default_checkbox: { // Checkbox
    values: {
      type: 'checkbox'
    },
    error: {}
  },
  custom_offer_subtotal: {
    error: {
      constraints: {
        presence: 'optional',
        numericality: {
          greaterThanOrEqualTo: 1
        }
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  terms_of_service: {
    error: {
      constraints: {
        presence: {
          message: 'Please accept our terms of service.'
        }
      },
      errorIcon: 'icon-no',
      validIcon: 'icon-yes'
    }
  },
  email: {
    error: {
      constraints: {
        presence: {
          message: errorData.email_empty_error
        },
        email: {
          message: errorData.email_valid_error
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  password: {
    error: {
      constraints: {
        presence: {
          message: errorData.password_empty_error
        },
        length: {
          minimum: 8,
          message: errorData.password_format_error
        },
        format: {
          pattern: '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{0,}$',
          flags: 'i',
          message: errorData.password_format_error
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  password_no_msg: {
    error: {
      constraints: {
        presence: {
          message: null
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  first_name: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: <FormattedMessage id="shared_components.input_error.enter_first_name" defaultMessage="Please enter your first name." />
        },
        length: {
          minimum: 2,
          tooShort: <FormattedMessage id="shared_components.input_error.error_first_name_short" defaultMessage="Is too short (minimum is 2 characters)" />,
          maximum: 35,
          tooLong: <FormattedMessage id="shared_components.input_error.error_first_name_long" defaultMessage="Is too long (maximum is 35 characters)" />
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  last_name: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: <FormattedMessage id="shared_components.input_error.enter_last_name" defaultMessage="Please enter your last name." />
        },
        length: {
          minimum: 2,
          tooShort: <FormattedMessage id="shared_components.input_error.error_last_name_short" defaultMessage="Is too short (minimum is 2 characters)" />,
          maximum: 35,
          tooLong: <FormattedMessage id="shared_components.input_error.error_last_name_long" defaultMessage="Is too long (maximum is 35 characters)" />
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  login_pw: {
    error: {
      constraints: {
        presence: {
          message: errorData.password_empty_error
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  address1: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Please enter your street address or PO Box.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  address2: {
    error: {
      constraints: {
        presence: {
          message: 'optional'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  city: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Please enter your city.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  state: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Please select your state.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  zipcode: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'optional'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  organization: {
    error: {
      constraints: {
        presence: {
          message: 'optional'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  job_title: {
    error: {
      constraints: {
        presence: {
          message: 'optional'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  mobile_phone: {
    error: {
      constraints: {
        presence: {
          message: 'optional'
        },
        format: {
          pattern: /\d{10}/,
          message: 'Please enter a valid phone number with no special characters.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  verification_code: {
    error: {
      constraints: {
        presence: {
          message: errorData.email_invalid_code_error
        },
        format: {
          pattern: /\d{4}/,
          message: errorData.email_invalid_code_error
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  attendees: {
    error: {
      constraints: {
        presence: {
          message: errorData.attendees_empty
        },
        numericality: {
          greaterThan: 0,
          lessThanOrEqualTo: 100000,
          message: errorData.attendees_range_error
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  review_response: {
    error: {
      constraints: {
        presence: {
          message: <FormattedMessage id="shared_components.review.error_review_empty" defaultMessage="Please fill in your response" />
        },
        length: {
          tooShort: <FormattedMessage id="shared_components.review.error_review_short" defaultMessage="Your review must be more than 5 characters long" />,
          minimum: 5,
          tooLong: <FormattedMessage id="shared_components.review.error_review_long" defaultMessage="Your review must be less than 1500 characters long" />,
          maximum: 1500
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  required_textbox_response: { // text area > 5 characters < 1000 characters
    error: {
      constraints: {
        presence: {
          message: errorData.empty_input_error
        },
        length: {
          minimum: 5,
          tooShort: 'Minimum is %{count} characters',
          maximum: 1000,
          tooLong: 'Maximum is %{count} characters'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  msg_host: { // text area
    error: {
      constraints: {
        presence: {
          message: errorData.msg_host
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  flex_time: { // checkbox
    error: {
      constraints: {
        presence: {
          message: 'optional'
        }
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  cardholders_name: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: errorData.enter_name_error
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  cardnumber: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Enter a card number.'
        },
        length: {
          minimum: 13,
          tooShort: 'Please enter a number that is %{count} digits or more.',
          maximum: 19, // allow for space
          tooLong: 'Please enter a number that is %{count} digits or fewer.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  month: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Enter a month.'
        },
        length: {
          is: 2,
          message: 'Enter a month in MM format.'
        },
        format: {
          pattern: '^(0[1-9]|1[0-2])$',
          message: 'Enter a valid month.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  day: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Enter a day.'
        },
        length: {
          is: 2,
          message: 'Enter a day in DD format.'
        },
        format: {
          pattern: '^(([0]?[1-9])|([1-2][0-9])|(3[01]))$',
          message: 'Enter a valid day of the month.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  year: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Enter a year.'
        },
        length: {
          is: 4,
          message: 'Enter a year in YYYY format.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  cvv: {
    error: {
      constraints: {
        presence: {
          message: 'Enter security code.'
        },
        length: {
          minimum: 3,
          tooShort: 'Minimum is %{count} characters.',
          maximum: 4,
          tooLong: 'Maximum is %{count} characters.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  account_holder: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Please enter the legal name associated with your bank account.'
        },
        length: {
          minimum: 3,
          tooShort: 'Minimum is %{count} characters.',
          maximum: 300,
          tooLong: 'Maximum is %{count} characters.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  account_number: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Bank account number is required.'
        },
        format: {
          pattern: /\d{3,17}/,
          message: 'Your account number should be 3 - 17 numbers without punctuation.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  routing_number: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Routing number is required.'
        },
        format: {
          pattern: /\d{9}/,
          message: 'Enter your routing number as 9 numbers without punctuation.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  account_number_ca: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Bank account number is required.'
        },
        format: {
          pattern: /\d{7,12}/,
          message: 'Your account number should be 7 - 12 numbers without punctuation.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  transit_number: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Transit number is required.'
        },
        format: {
          pattern: /\d{5}/,
          message: 'Enter your transit number as 5 numbers without punctuation.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  institution_number: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Institution number is required.'
        },
        format: {
          pattern: /\d{3}/,
          message: 'Enter your institution number as 3 numbers without punctuation.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  account_number_uk: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Bank account number is required.'
        },
        format: {
          pattern: /\d{8}/,
          message: 'Enter your bank account as 8 numbers without punctuation.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  sort_code: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Sort code is required.'
        },
        format: {
          pattern: /\d{6}/,
          message: 'Enter your sort code as 6 numbers without punctuation.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  csrf_token_input: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  hidden_account_type: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  hidden_country_code: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  hidden_currency_code: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  hidden_routing_number: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: '',
      inputBorder: '',
      optional: null,
      validIcon: ''
    }
  },
  tax_id: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Please add your Social Security Number.'
        },
        format: {
          pattern: /\d{9}/,
          message: 'Enter your Social Security Number as 9 numbers with no punctuation.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  business_name: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Please enter the name of your business.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  last_4: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: 'Please enter the last 4 digits of your social security number.'
        },
        format: {
          pattern: /\d{4}/,
          message: 'Please enter the last 4 digits of your social security number.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  board_name: {
    error: {
      constraints: {
        presence: {
          message: errorData.board_name_empty_error
        },
        length: {
          minimum: 2,
          tooShort: errorData.board_name_min,
          maximum: 35,
          tooLong: errorData.board_name_max
        },
        custom: true
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  board_description: {
    error: {
      constraints: {
        length: {
          maximum: 130,
          tooLong: 'Maximum is %{count} characters.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  activity: {
    error: {
      constraints: {
        presence: {
          message: 'Please select an activity type.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  location: {
    error: {
      constraints: {
        presence: {
          message: 'Please enter a location.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  meta_email: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  personal_message: {
    error: {
      constraints: {
        presence: {
          message: errorData.personal_message
        },
        length: {
          minimum: 15,
          tooShort: errorData.personal_message_min,
          maximum: 1000,
          tooLong: errorData.personal_message_max
        },
        custom: true
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  concierge_budget: {
    error: {
      constraints: {
        presence: {
          message: 'Please enter your budget.'
        },
        numericality: {
          message: 'Please enter a dollar amount.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  other_concierge_items: {
    error: {
      constraints: {
        presence: {
          message: 'Please enter a message.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  concierge_details: {
    error: {
      constraints: {
        presence: 'optional'
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  comment: {
    error: {
      constraints: {
        presence: {
          message: errorData.comment_empty_error
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      optional: null,
      validIcon: 'icon-yes'
    }
  },
  other_how_did_you_hear: {
    error: {
      constraints: {
        allowEmpty: false,
        presence: 'optional'
      },
      validIcon: 'icon-yes'
    }
  },
  how_did_you_hear: {
    values: {
      type: 'checkbox'
    },
    error: {}
  },
  block_day_title: {
    error: {
      constraints: {
        presence: {
          message: 'Please enter a title.'
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  all_day: { // Checkbox
    values: {
      type: 'checkbox'
    },
    error: {}
  },
  iban_number: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: errorData.iban_empty_error,
        },
        format: {
          pattern: /[A-Z0-9]{5,34}/,
          message: errorData.iban_format_error,
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  bsb_number: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: errorData.bsb_number_empty_error,
        },
        format: {
          pattern: /\d{6}/,
          message: errorData.bsb_number_format_error,
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
  account_number_au: {
    error: {
      constraints: {
        presence: {
          allowEmpty: false,
          message: errorData.account_number_au_empty_error,
        },
        format: {
          pattern: /\d{5,9}/,
          message: errorData.account_number_au_format_error,
        }
      },
      errorIcon: 'icon-no',
      inputBorder: 'error',
      validIcon: 'icon-yes'
    }
  },
};
